/**
 * InstructorIdentity — Avatar + name, shared by mobile (picker row, confirm
 * card, success card) and web (carousel card, confirm mini-card). [BUT-843]
 *
 * Four variants reproduce each call site's existing DOM/classes exactly:
 * `row` (mobile picker row), `hero` (mobile confirm/success), `card` (web
 * carousel), `compact` (web confirm mini-card / details card).
 *
 * `unresolved`: when the caller couldn't resolve a real instructor and `name`
 * is the "Your instructor" placeholder, the Avatar must not derive fabricated
 * initials ("YI") from it — pass `name=""` so `Avatar` renders its neutral
 * gradient circle instead. The visible text label still reads the placeholder.
 *
 * `label` adds a "Lesson with"-style line above the identity. Without it the
 * DOM is byte-identical to before.
 *
 * `image` is the wire `ImageData` ({ url, alt? }); `alt` falls back to `name`.
 */

import type { ImageData } from '@lgc/database/types/domains/storage'
import { Avatar } from '#primitives/data-display'

export type InstructorIdentityVariant = 'row' | 'hero' | 'card' | 'compact'

export interface InstructorIdentityProps {
  name: string
  image?: ImageData | null
  variant: InstructorIdentityVariant
  /** True when `name` is an unresolved placeholder, not a real instructor. */
  unresolved?: boolean
  className?: string
  /** Line above the identity (e.g. "Lesson with"), 14 Medium, 20px to the avatar. */
  label?: string
}

const ROOT: Record<InstructorIdentityVariant, string> = {
  row: 'flex items-center gap-2',
  hero: 'flex items-center gap-3 flex-col text-center',
  card: 'flex flex-row items-center gap-2.5 md:flex-col md:gap-4',
  compact: 'flex flex-row items-center gap-3',
}

const RING: Record<InstructorIdentityVariant, string> = {
  row: 'shrink-0 rounded-full',
  hero: 'shrink-0 rounded-full border border-butter-gray-200',
  card: 'inline-flex shrink-0 rounded-full',
  compact: 'inline-flex shrink-0 rounded-full border border-butter-gray-200',
}

const NAME: Record<InstructorIdentityVariant, string> = {
  row: 'text-base font-semibold leading-5 text-butter-gray-800',
  hero: 'text-lg font-semibold leading-tight text-butter-gray-800',
  card: 'text-base font-semibold text-butter-gray-800 md:text-xl md:leading-6',
  compact: 'text-base font-semibold text-butter-gray-800 md:text-lg',
}

// row/hero use Avatar's size preset; card/compact override with an explicit
// className (no size → md → text-sm initials, matching the old web copy).
const AVATAR_SIZE: Record<InstructorIdentityVariant, 'md' | 'xl' | undefined> = {
  row: 'md',
  hero: 'xl',
  card: undefined,
  compact: undefined,
}

const AVATAR_CLASSNAME: Record<InstructorIdentityVariant, string | undefined> = {
  row: 'h-10 w-10 border border-butter-gray-200',
  hero: undefined,
  card: 'h-12 w-12 border border-butter-gray-200 md:h-20 md:w-20',
  compact: 'h-10 w-10 md:h-11 md:w-11',
}

const LABELLED: Record<InstructorIdentityVariant, string> = {
  row: 'flex flex-col gap-5',
  hero: 'flex flex-col items-center gap-5 text-center',
  card: 'flex flex-col gap-5',
  compact: 'flex flex-col gap-5',
}

export function InstructorIdentity({
  name,
  image,
  variant,
  unresolved = false,
  className,
  label,
}: InstructorIdentityProps) {
  const root = (
    <div
      className={label ? ROOT[variant] : `${ROOT[variant]}${className ? ` ${className}` : ''}`}
      data-testid="lesson-instructor-identity"
    >
      <div className={RING[variant]}>
        <Avatar
          name={unresolved ? '' : name}
          src={image?.url}
          alt={image?.alt}
          size={AVATAR_SIZE[variant]}
          className={AVATAR_CLASSNAME[variant]}
        />
      </div>
      <span className={NAME[variant]}>{name}</span>
    </div>
  )
  if (!label) return root
  return (
    <div className={`${LABELLED[variant]}${className ? ` ${className}` : ''}`}>
      <p className="text-sm font-medium text-butter-gray-700">{label}</p>
      {root}
    </div>
  )
}
